import dagre from "@dagrejs/dagre";
import { Position, type Edge, type Node } from "@xyflow/react";

import type { FlowNodeType } from "./nodes/types";

const NODE_SIZES: Record<FlowNodeType, { width: number; height: number }> = {
  order: { width: 260, height: 150 },
  customer: { width: 260, height: 140 },
  orderItem: { width: 260, height: 140 },
  cargo: { width: 260, height: 145 },
  product: { width: 260, height: 120 },
  warehouse: { width: 260, height: 108 },
  payment: { width: 260, height: 156 },
  delivery: { width: 260, height: 130 },
  contract: { width: 260, height: 125 },
  document: { width: 260, height: 105 },
};

const DEFAULT_SIZE = { width: 260, height: 130 };
const LAYOUT_CONFIG = {
  rankdir: "LR" as const,
  ranksep: 200,
  nodesep: 84,
  marginx: 32,
  marginy: 32,
  sourcePosition: Position.Right,
  targetPosition: Position.Left,
};

const STRAIGHT_ALIGNMENT_RULES: Array<{
  sourceType: FlowNodeType;
  targetType: FlowNodeType;
  move: "source" | "target";
  onlyIfSourceHasSingleOutgoing?: boolean;
}> = [
  { sourceType: "customer", targetType: "order", move: "source" },
  { sourceType: "orderItem", targetType: "cargo", move: "target" },
  {
    sourceType: "cargo",
    targetType: "warehouse",
    move: "target",
    onlyIfSourceHasSingleOutgoing: true,
  },
  {
    sourceType: "cargo",
    targetType: "product",
    move: "target",
    onlyIfSourceHasSingleOutgoing: true,
  },
];

function getNodeSize(type?: FlowNodeType) {
  return (type && NODE_SIZES[type]) || DEFAULT_SIZE;
}

export function applyDagreLayout<TData extends Record<string, unknown>>(
  nodes: Array<Node<TData, FlowNodeType>>,
  edges: Edge[],
): { nodes: Array<Node<TData, FlowNodeType>>; edges: Edge[] } {
  const graph = new dagre.graphlib.Graph();
  graph.setDefaultEdgeLabel(() => ({}));
  graph.setGraph({
    rankdir: LAYOUT_CONFIG.rankdir,
    ranksep: LAYOUT_CONFIG.ranksep,
    nodesep: LAYOUT_CONFIG.nodesep,
    marginx: LAYOUT_CONFIG.marginx,
    marginy: LAYOUT_CONFIG.marginy,
  });

  for (const node of nodes) {
    const size = (node.type && NODE_SIZES[node.type]) || DEFAULT_SIZE;
    graph.setNode(node.id, size);
  }

  for (const edge of edges) {
    graph.setEdge(edge.source, edge.target);
  }

  dagre.layout(graph);

  const layoutedNodes = nodes.map((node) => {
    const size = getNodeSize(node.type);
    const positioned = graph.node(node.id);

    return {
      ...node,
      position: {
        x: positioned.x - size.width / 2,
        y: positioned.y - size.height / 2,
      },
      sourcePosition: LAYOUT_CONFIG.sourcePosition,
      targetPosition: LAYOUT_CONFIG.targetPosition,
    };
  });

  const nodesById = new Map(layoutedNodes.map((node) => [node.id, node]));
  const outgoingCounts = new Map<string, number>();

  for (const edge of edges) {
    outgoingCounts.set(edge.source, (outgoingCounts.get(edge.source) ?? 0) + 1);
  }

  for (const edge of edges) {
    const sourceNode = nodesById.get(edge.source);
    const targetNode = nodesById.get(edge.target);
    if (!sourceNode || !targetNode) continue;

    const rule = STRAIGHT_ALIGNMENT_RULES.find(
      (candidate) =>
        candidate.sourceType === sourceNode.type && candidate.targetType === targetNode.type,
    );
    if (!rule) continue;

    if (rule.onlyIfSourceHasSingleOutgoing && (outgoingCounts.get(sourceNode.id) ?? 0) !== 1) {
      continue;
    }

    const anchorNode = rule.move === "target" ? sourceNode : targetNode;
    const movedNode = rule.move === "target" ? targetNode : sourceNode;
    const anchorSize = getNodeSize(anchorNode.type);
    const movedSize = getNodeSize(movedNode.type);
    const anchorCenterY = anchorNode.position.y + anchorSize.height / 2;

    movedNode.position = {
      ...movedNode.position,
      y: anchorCenterY - movedSize.height / 2,
    };
  }

  return { nodes: layoutedNodes, edges };
}
